import { useEffect, useState } from "react";
import { TProcessStates } from "../../../lib/types/general";
import { LabSignUpFormData, TApiLab, TRestaurant } from "../../../lib/types/labs";
import { useLabs } from "../../../services/laboratories";
import { Spinner } from "../../common/spinner";
import { DEVICES_SIZES_IN_PX } from "../../../lib/common/constants";
import { LifestyleOverlayCard } from "./lifestyle-overlay-card";

type TRestaurantsProps = {
    editable: boolean;
    isSomePartBeingEdited: boolean;
    userInfo: TApiLab;
    workingState: TProcessStates;
    setUserInfo: React.Dispatch<React.SetStateAction<TApiLab>>;
    setIsSomePartBeingEdited: (value: React.SetStateAction<boolean>) => void;
    updateLabAndLocalStorage: (data: LabSignUpFormData, name?: string) => Promise<void>;
};

export const Restaurants = ({
    editable,
    isSomePartBeingEdited,
    userInfo,
    workingState,
    setUserInfo,
    setIsSomePartBeingEdited,
    updateLabAndLocalStorage,
}: TRestaurantsProps) => {
    const [restaurantsPerPage, setRestaurantsPerPage] = useState(5);
    const totalRestaurants = userInfo.other.aboutRestaurants || [];
    const totalItems = Math.ceil(totalRestaurants.length / restaurantsPerPage);
    const [visibleRestaurants, setVisibleRestaurants] = useState(5);

    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth <= DEVICES_SIZES_IN_PX.medium) {
                setRestaurantsPerPage(3);
            } else {
                setRestaurantsPerPage(5);
            }
        };
        handleResize();
        window.addEventListener("resize", handleResize);
        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    const renderCarouselItems = () => {
        const carouselItems = [];

        for (let i = 0; i < totalItems; i++) {
            const startIdx = i * restaurantsPerPage;
            const endIdx = startIdx + restaurantsPerPage;
            const currentRestaurants = totalRestaurants.slice(startIdx, endIdx);

            const cardGroup = currentRestaurants.map((restaurant) => (
                <div className="col" key={restaurant.id}>
                    {/* non editable carrousel */}
                    <LifestyleOverlayCard
                        className="min-height-cards-lifestyle w-auto"
                        imageSrc={restaurant.image_url}
                        imageAlt="restaurant"
                        href={restaurant.url}
                        title={restaurant.name}
                        line1={
                            <>
                                <i className="fa-solid fa-star text-primary me-2"></i> {restaurant.rating}
                            </>
                        }
                        line2={
                            <>
                                <i className="fa-solid fa-location-dot me-2"></i>
                                {restaurant.location?.address1 || "Address"}
                            </>
                        }
                        line3={
                            <>
                                <i className="fa-solid fa-utensils me-2"></i>
                                {restaurant.categories?.[0]?.title || "Category"}
                            </>
                        }
                    />
                </div>
            ));

            //hack to make all the card equally width when there is less than what the page allows
            if (cardGroup.length < restaurantsPerPage) {
                const remainingCards = restaurantsPerPage - cardGroup.length;
                for (let j = 0; j < remainingCards; j++) {
                    const DummyEmptyCard = <div className="col" key={`restaurant-empty-${i}-${j}`} aria-hidden="true"></div>;
                    cardGroup.push(DummyEmptyCard);
                }
            }
            carouselItems.push(
                <div className={`carousel-item ${i === 0 ? "active" : ""}`} key={i}>
                    <div className={"row g-3 row-cols-1 row-cols-md-" + restaurantsPerPage}>{cardGroup}</div>
                </div>,
            );
        }

        return carouselItems;
    };
    const { getRestaurants } = useLabs();

    const fetchRestaurantsData = async () => {
        const zip = userInfo.other?.zip || "";
        if (!zip || zip === "") {
            return;
        }
        setCurrWorkingStateRestaurants("working");
        try {
            const response = await getRestaurants(zip);
            const restaurants = response.data?.businesses || [];
            const userInfoSelectedRestaurantsMap = userInfo.other?.aboutRestaurants
                ? userInfo.other.aboutRestaurants.reduce(
                      (prev, curr) => {
                          return {
                              ...prev,
                              [curr.id]: curr,
                          };
                      },
                      {} as Record<string, any>,
                  )
                : {};
            //we set the selected restaurants
            restaurants.forEach((r) => {
                r.isSelected = userInfoSelectedRestaurantsMap[r.id] !== undefined ? true : false;
            });
            setSelectedRestaurants(restaurants);
        } catch (error) {
            console.error("Error fetching data:", error);
        } finally {
            setCurrWorkingStateRestaurants("idle");
        }
    };

    const [isExpandedRestaurant, setIsExpandedRestaurant] = useState(false);
    const [selectedRestaurants, setSelectedRestaurants] = useState<TRestaurant[]>([]);
    const [currWorkingStateRestaurants, setCurrWorkingStateRestaurants] = useState<TProcessStates>("done");
    const openRestaurantOptions = () => {
        setIsExpandedRestaurant(!isExpandedRestaurant);
        fetchRestaurantsData();
        setIsSomePartBeingEdited(true);
    };
    const handleCheckboxChange = (e: any, restaurant: TRestaurant) => {
        e.stopPropagation();
        setSelectedRestaurants((prev) => {
            const idx = prev.findIndex((r) => r.id === restaurant.id);
            prev[idx].isSelected = !prev[idx].isSelected;
            return prev;
        });
    };

    const saveRestaurants = async (e: any) => {
        e.preventDefault();
        e.stopPropagation();

        setCurrWorkingStateRestaurants("working");
        const selectedInfo = selectedRestaurants.filter((r) => r.isSelected);
        const newRestaurants = {
            ...userInfo.other,
            aboutRestaurants: selectedInfo,
        };

        await updateLabAndLocalStorage(
            {
                password: "",
                ...newRestaurants,
            },
            "",
        );

        setUserInfo({
            ...userInfo,
            other: {
                ...userInfo.other,
                aboutRestaurants: selectedInfo,
            },
        });

        setIsExpandedRestaurant(false);
        setIsSomePartBeingEdited(false);
        setCurrWorkingStateRestaurants("done");
    };

    const cancelHandler = async () => {
        setIsExpandedRestaurant(false);
        setIsSomePartBeingEdited(false);
    };

    return (
        <>
            {!editable && userInfo.other.aboutRestaurants && (
                <div className="container mb-4 py-3 lifestyle-panel">
                    <div className="row">
                        {/* non editable desktop carrousel - techs view*/}
                        <h6 className="mb-3 fw-bold text-primary">FAVORITE RESTAURANTS</h6>
                        <div className="d-none d-md-block">
                            <div className="min-height-cards-lifestyle w-100">
                                {workingState === "working" && <p className="p-3">Looking for restaurants in this area...</p>}
                                {["idle", "done"].includes(workingState) && userInfo.other.aboutRestaurants && userInfo.other.aboutRestaurants.length > 0 && (
                                    <div id="restCarousel" className="carousel slide" data-bs-ride="carousel" data-bs-interval="false">
                                        <div className="carousel-inner">{renderCarouselItems()}</div>
                                        {userInfo.other.aboutRestaurants.length > 5 && (
                                            <div className="">
                                                <a className="carousel-control-prev" href="#restCarousel" role="button" data-bs-slide="prev">
                                                    <span className="carousel-control-prev-icon" aria-hidden="true"></span>
                                                    <span className="visually-hidden">Previous</span>
                                                </a>
                                                <a className="carousel-control-next" href="#restCarousel" role="button" data-bs-slide="next">
                                                    <span className="carousel-control-next-icon" aria-hidden="true"></span>
                                                    <span className="visually-hidden">Next</span>
                                                </a>
                                            </div>
                                        )}
                                    </div>
                                )}
                                {["idle", "done"].includes(workingState) && userInfo.other.aboutRestaurants?.length === 0 && <p>No restaurants found.</p>}
                            </div>
                        </div>
                        {/* non editable mobile - this is not a carrousel, its a stack of cards - techs view*/}
                        <div className="d-block d-md-none">
                            {workingState === "working" && <p>Looking for restaurants in this area...</p>}
                            {["idle", "done"].includes(workingState) && userInfo.other.aboutRestaurants && userInfo.other.aboutRestaurants.length > 0 && (
                                <>
                                    {userInfo.other.aboutRestaurants.slice(0, visibleRestaurants).map((restaurant) => (
                                        <div key={restaurant.id} className="my-2">
                                            <LifestyleOverlayCard
                                                className="mb-3"
                                                imageSrc={restaurant.image_url}
                                                imageAlt="restaurant"
                                                href={restaurant.url}
                                                title={restaurant.name}
                                                line1={
                                                    <>
                                                        <i className="fa-solid fa-star text-primary"></i> {restaurant.rating}
                                                    </>
                                                }
                                                line2={
                                                    <>
                                                        <i className="fa-solid fa-location-dot"></i>
                                                        {restaurant.location?.address1 || "Address"}
                                                    </>
                                                }
                                                line3={
                                                    <>
                                                        <i className="fa-solid fa-utensils"></i>
                                                        {restaurant.categories?.[0]?.title || "Category"}
                                                    </>
                                                }
                                            />
                                        </div>
                                    ))}
                                    {visibleRestaurants < userInfo.other.aboutRestaurants.length && (
                                        <div className="text-center">
                                            <button className="btn btn-link fw-semibold" onClick={() => setVisibleRestaurants((prev) => prev + 5)}>
                                                View More
                                            </button>
                                        </div>
                                    )}
                                </>
                            )}
                            {["idle", "done"].includes(workingState) && userInfo.other.aboutRestaurants?.length === 0 && <p>No restaurants found.</p>}
                        </div>
                    </div>
                </div>
            )}
            {editable && (
                <div className="container mb-4 py-3 lifestyle-panel">
                    <div className="row">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h6 className="mb-0 fw-bold text-primary">FAVORITE RESTAURANTS</h6>
                            {!isSomePartBeingEdited && (
                                <div
                                    className="fileTooltip rightEdit"
                                    data-tooltip=" Edit "
                                    role="button"
                                    onClick={openRestaurantOptions}
                                    aria-expanded="false"
                                    aria-controls="collapseRestaurant"
                                >
                                    <i data-bs-testid="edit-icon" className="fa-solid fa-pen-to-square fa-xs p-2 text-center shadow editIcon"></i>
                                </div>
                            )}
                        </div>
                        {isExpandedRestaurant && editable && (
                            <div>
                                <div className="card card-body mb-3">
                                    <p>
                                        {" "}
                                        <strong>Select the restaurants you would like to display.</strong>
                                    </p>
                                    <div>
                                        {currWorkingStateRestaurants === "working" && (
                                            <div className="text-center">
                                                <Spinner spinnerColor="text-primary" />
                                            </div>
                                        )}
                                        {currWorkingStateRestaurants === "idle" && selectedRestaurants.length > 0 && (
                                            <div className="row">
                                                {selectedRestaurants.map((restaurant) => (
                                                    <div className="col-md-6 col-lg-4 mb-3" key={restaurant.id}>
                                                        <div className="input-group h-100">
                                                            <label htmlFor={`rChk_${restaurant.id}`} className="w-100">
                                                                <div className="row h-100 w-100 align-items-center input-group-text p-2">
                                                                    <div className="col-2 p-0">
                                                                        <input
                                                                            type="checkbox"
                                                                            defaultChecked={restaurant.isSelected}
                                                                            id={`rChk_${restaurant.id}`}
                                                                            className="form-check-input"
                                                                            onChange={(e) => handleCheckboxChange(e, restaurant)}
                                                                        />
                                                                    </div>
                                                                    <div className="col-4 p-0">
                                                                        <img src={restaurant.image_url} className="rounded label-img" alt="restaurant" />
                                                                    </div>
                                                                    <div className="col-6 text-wrap">
                                                                        <p className="mb-1">
                                                                            {" "}
                                                                            <small>
                                                                                <strong>{restaurant.name}</strong>
                                                                            </small>
                                                                        </p>
                                                                        <p
                                                                            className="mb-0"
                                                                            style={{
                                                                                fontSize: "x-small",
                                                                            }}
                                                                        >
                                                                            {restaurant.categories?.map((category, index) => (
                                                                                <span key={index}>
                                                                                    {category.title}
                                                                                    {index < (restaurant.categories || []).length - 1 && ", "}
                                                                                </span>
                                                                            ))}
                                                                        </p>
                                                                    </div>
                                                                </div>
                                                            </label>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                        {currWorkingStateRestaurants === "idle" && selectedRestaurants.length === 0 && (
                                            <p>No restaurants found in this area.</p>
                                        )}
                                    </div>
                                    <div className="d-grid gap-3 mt-2 w-100 d-md-block">
                                        <button className="btn btn-primary px-4" type="button" onClick={saveRestaurants}>
                                            Save
                                        </button>
                                        <button className="btn btn-danger ms-md-3 px-3" type="button" onClick={cancelHandler}>
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                        {/* editable desktop carrousel - labs view*/}
                        <div className="d-none d-md-block">
                            <div className="min-height-cards-lifestyle w-100">
                                {workingState === "working" && <p className="p-3">Looking for restaurants in this area...</p>}
                                {["idle", "done"].includes(workingState) && userInfo.other.aboutRestaurants && userInfo.other.aboutRestaurants.length > 0 && (
                                    <div id="restCarousel" className="carousel slide" data-bs-ride="carousel" data-bs-interval="false">
                                        <div className="carousel-inner">{renderCarouselItems()}</div>
                                        {userInfo.other.aboutRestaurants.length > 5 && (
                                            <div className="">
                                                <a className="carousel-control-prev" href="#restCarousel" role="button" data-bs-slide="prev">
                                                    <span className="carousel-control-prev-icon" aria-hidden="true"></span>
                                                    <span className="visually-hidden">Previous</span>
                                                </a>
                                                <a className="carousel-control-next" href="#restCarousel" role="button" data-bs-slide="next">
                                                    <span className="carousel-control-next-icon" aria-hidden="true"></span>
                                                    <span className="visually-hidden">Next</span>
                                                </a>
                                            </div>
                                        )}
                                    </div>
                                )}
                                {["idle", "done"].includes(workingState) && userInfo.other.aboutRestaurants?.length === 0 && <p>No restaurants found.</p>}
                            </div>
                        </div>
                        {/* editable mobile - this is not a carrousel, its a stack of cards - techs view*/}
                        <div className="d-block d-md-none">
                            {workingState === "working" && <p>Looking for restaurants in this area...</p>}
                            {["idle", "done"].includes(workingState) && userInfo.other.aboutRestaurants && userInfo.other.aboutRestaurants.length > 0 && (
                                <>
                                    {userInfo.other.aboutRestaurants.slice(0, visibleRestaurants).map((restaurant) => (
                                        <div className="my-2" key={restaurant.id}>
                                            <LifestyleOverlayCard
                                                className="mb-3"
                                                imageSrc={restaurant.image_url}
                                                imageAlt="restaurant"
                                                href={restaurant.url}
                                                title={restaurant.name}
                                                line1={
                                                    <>
                                                        <i className="fa-solid fa-star text-primary me-2"></i> {restaurant.rating}
                                                    </>
                                                }
                                                line2={
                                                    <>
                                                        <i className="fa-solid fa-location-dot me-2"></i>
                                                        {restaurant.location?.address1 || "Address"}
                                                    </>
                                                }
                                                line3={
                                                    <>
                                                        <i className="fa-solid fa-utensils me-2"></i>
                                                        {restaurant.categories?.[0]?.title || "Category"}
                                                    </>
                                                }
                                            />
                                        </div>
                                    ))}
                                    {visibleRestaurants < userInfo.other.aboutRestaurants.length && (
                                        <div className="text-center">
                                            <button className="btn btn-link fw-semibold" onClick={() => setVisibleRestaurants((prev) => prev + 5)}>
                                                View More
                                            </button>
                                        </div>
                                    )}
                                </>
                            )}
                            {["idle", "done"].includes(workingState) && userInfo.other.aboutRestaurants?.length === 0 && <p>No restaurants found.</p>}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};
